import React, { useEffect, useMemo } from 'react';
import { PlusIcon } from '@radix-ui/react-icons';
import { observer } from 'mobx-react-lite';
import { ElevatedButton } from '@producer/ui-kit';

import { Header } from './project.page';
import { useCurrentProject } from '../../hooks/use-current-project';
import { uiStore } from '../../core/stores/ui-store';

import { TaskList } from '../../features/tasks/components/tasks-list';
import { CreateTaskModal } from '../../features/tasks/components/create-task.modal';
import { PagePlaceholder } from '../../components/page-placeholder/page-placeholder';
import { PageLoader } from '../../app/components/page-loader';
import { taskGroupingStore } from '../../core/stores/task-store';
import { openModal } from '../../core/modal/open-modal';

export const ProjectTasksRoute: React.FC = observer(() => {
  const project = useCurrentProject()!;

  const projectTasks = project.tasks;
  const projectTasksFilter = project.tasksFilter;

  const filteredTasks = useMemo(() => {
    if (!projectTasksFilter.filtersCount) {
      return projectTasks;
    }

    const { status, priority, assigneeIds, tagIds } = projectTasksFilter;

    return projectTasks.filter((task) => {
      if (status.length && !status.includes(task.status)) {
        return false;
      }

      if (priority.length && !priority.includes(task.priority)) {
        return false;
      }

      if (assigneeIds.length && !assigneeIds.includes(task.assigneeId)) {
        return false;
      }

      if (tagIds.length) {
        if (tagIds.includes(null) && task.tags.length === 0) {
          return true;
        }

        if (!task.tags.some((tag) => tagIds.includes(tag._id))) {
          return false;
        }
      }

      return true;
    });
  }, [projectTasks, projectTasksFilter.filtersCount, projectTasksFilter]);

  useEffect(() => {
    uiStore.currentLocation = 'project.tasks';

    return () => {
      uiStore.currentLocation = '';
    };
  }, []);

  const openCreateModal = () =>
    openModal(CreateTaskModal, {
      title: 'Create task',
      project,
    });

  const content = () => {
    if (uiStore.isDataFetching) {
      return <PageLoader />;
    }

    if (project.tasks.length === 0) {
      return (
        <PagePlaceholder
          title="You're off to a clean start!"
          description="Start by creating a task to keep things organized and on track."
          fullWidth
          backgroundImgUrl="/images/order-from-chaos.svg"
        />
      );
    }

    return (
      <TaskList
        key={taskGroupingStore.groupingKey}
        tasks={filteredTasks}
        onClearFilters={() => projectTasksFilter.clearFilters()}
        taskLevel="project"
      />
    );
  };

  return (
    <>
      <Header>
        <span>Tasks</span>

        <ElevatedButton
          id="create-task-button"
          icon={<PlusIcon />}
          text="Create task"
          onClick={openCreateModal}
        />
      </Header>

      {content()}
    </>
  );
});
